import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable, type RowAction } from "@/components/data-table/DataTable";
import { usePagedQuery } from "@/components/data-table/usePagedQuery";
import { deleteLayoutNotificacao, LAYOUT_NOTIFICACAO_PATH, type LayoutNotificacao, type LayoutNotificacaoFiltro } from "./api";

// Porte de app/controllers/consulta-layout-notificacao.controller.js +
// app/templates/notificacao/consulta-layout-notificacao.html. O modal
// "incluir/editar" do template legado está morto: o body do modal está
// vazio no HTML e nem "Adicionar Novo" (link cru) nem a coluna Ações
// (link cru pra /LayoutNotificacao/Form/:id) chamam incluir()/editar() —
// só confirmarExclusao() de fato roda. Reproduzido como consulta pura.
// /LayoutNotificacao/Form(/:id) agora também é React — "Editar"/"Adicionar
// Novo" usam navegação client-side (useNavigate/Link) em vez de reload.
const QUERY_KEY = ["layout-notificacao"];

const SITUACAO_LABEL: Record<string, string> = { A: "Ativa", I: "Inativo" };

const columns: ColumnDef<LayoutNotificacao, unknown>[] = [
  { accessorKey: "id", header: "ID", enableSorting: true },
  { accessorKey: "gatilho", header: "Gatilho", enableSorting: true },
  { accessorKey: "descricao", header: "Descrição", enableSorting: true },
  {
    accessorKey: "situacao",
    header: "Situação",
    cell: ({ getValue }) => SITUACAO_LABEL[String(getValue() ?? "")] ?? "Indefinido",
  },
];

export default function ConsultaLayoutNotificacao() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const query = usePagedQuery<LayoutNotificacao, LayoutNotificacaoFiltro>({
    key: QUERY_KEY,
    path: LAYOUT_NOTIFICACAO_PATH,
    initialFiltro: {},
  });

  const excluir = useMutation({
    mutationFn: deleteLayoutNotificacao,
    onSuccess: () => {
      toast.success("Layout de notificação excluído.");
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const actions: RowAction<LayoutNotificacao>[] = [
    { label: "Editar", icon: Pencil, onClick: (row) => navigate(`/LayoutNotificacao/Form/${row.id}`) },
    {
      label: "Excluir",
      icon: Trash2,
      variant: "destructive",
      onClick: (row) => excluir.mutate(row.id),
      confirm: {
        title: "Excluir layout de notificação?",
        description: (row) => `"${row.descricao}" será removido permanentemente.`,
        confirmLabel: "Excluir",
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-foreground">Layouts de Notificação</h1>
        <Button onClick={() => navigate("/LayoutNotificacao/Form")}>
          <Plus className="mr-2 h-4 w-4" /> Adicionar Novo
        </Button>
      </div>

      <DataTable
        query={query}
        columns={columns}
        actions={actions}
        getRowId={(r) => r.id}
        exportFilename="layouts-de-notificacao"
        emptyMessage="Nenhum layout de notificação encontrado."
        toolbar={
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-64">
              <Label htmlFor="f-descricao">Descrição</Label>
              <Input
                id="f-descricao"
                value={query.draft.descricao ?? ""}
                onChange={(e) => query.setDraft((f) => ({ ...f, descricao: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && query.search()}
              />
            </div>
            <Button onClick={query.search}>Buscar</Button>
          </div>
        }
      />
    </div>
  );
}
